/**
 * Universal tab content editor used by Finance, Démarches, Comptabilité, Documents.
 * - Loads schema (fields + workflow) from /api/configuration/tab-schema/{tab}
 * - Loads per-dossier values from /api/dossiers/{id}/tab-data/{tab}
 * - Renders each field with its type-appropriate input + status (libre or dropdown)
 * - Renders attachments (upload + list + delete) per field
 * - Renders horizontal workflow bar at the top
 * - Debounced autosave of values + statuses
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, X, Loader2, FileText, Link2, UserPlus, UserMinus, User, Users, Banknote } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import TabWorkflowBar from "./TabWorkflowBar";
import { FilesField, CallClientButton } from "./installation/InstallFields";
import { useMateriel, matLabel, pvAutofill, pvWarning } from "./installation/materiel";

const prodKey = (p) => { const u = (p || "").trim().toUpperCase(); return ["PAC", "PV", "ITE"].find((k) => u.startsWith(k)) || u; };
const isVisible = (f, values, produit) => !f.hidden && (!f.produits?.length || f.produits.includes(prodKey(produit)))
  && (!f.show_if || (f.show_if.values || []).includes(values?.[f.show_if.field]));

const SAVE_DEBOUNCE_MS = 600;

function MaterielSelect({ field, value, onChange, testid, className }) {
  const mat = useMateriel();
  const items = (mat?.[field.source] || []).map(matLabel);
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={className} data-testid={testid}>
      <option value="">—</option>
      {value && !items.includes(value) && <option value={value}>{value} (hors liste)</option>}
      {items.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

export function FieldInput({ field, value, onChange, testid }) {
  const common = "w-full px-2 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary focus:border-norea-primary outline-none";
  if (field.computed) {
    return <input readOnly value={value ?? 0} className={`${common} bg-norea-bg text-norea-textMuted cursor-not-allowed`} title="Calculé automatiquement" data-testid={testid} />;
  }
  if (field.type === "checkbox") {
    return (
      <label className="inline-flex items-center gap-2 text-sm text-norea-text cursor-pointer">
        <input type="checkbox" checked={value !== false && value != null} onChange={(e) => onChange(e.target.checked)} className="accent-norea-primary w-4 h-4" data-testid={testid} />
        {field.label}
      </label>
    );
  }
  if (field.type === "select") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid}>
        <option value="">—</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === "textarea") {
    return <textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid} />;
  }
  if (field.type === "email" || field.type === "phone") {
    return <input type={field.type === "email" ? "email" : "tel"} value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid} />;
  }
  if (field.type === "number" || field.type === "amount") {
    return <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={common} data-testid={testid} />;
  }
  if (field.type === "materiel") return <MaterielSelect field={field} value={value} onChange={onChange} testid={testid} className={common} />;
  if (field.type === "datetime") {
    return <input type="datetime-local" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid} />;
  }
  if (field.type === "date") {
    return <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid} />;
  }
  return <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={common} data-testid={testid} />;
}

function FieldStatusInput({ field, value, onChange, testid }) {
  if (field.status_type === "dropdown" && field.status_options?.length) {
    const colorMap = { "OK": "bg-emerald-50 border-emerald-400 text-emerald-800", "KO": "bg-red-50 border-red-300 text-red-800", "À faire": "bg-amber-50 border-amber-300 text-amber-800" };
    const cls = colorMap[value] || "bg-norea-surfaceHover border-norea-border text-norea-textMuted";
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={`text-[11px] px-1.5 py-0.5 rounded border ${cls} font-semibold`} data-testid={testid}>
        <option value="">Statut</option>
        {field.status_options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return <input placeholder="Statut libre" value={value || ""} onChange={(e) => onChange(e.target.value)} className="text-[11px] px-1.5 py-0.5 rounded border border-norea-border bg-white" data-testid={testid} />;
}

function AttachmentList({ dossierId, tab, fieldId, items, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("field_id", fieldId);
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/dossiers/${dossierId}/tab-data/${tab}/attachments`, fd);
      onChange?.();
      toast.success("Pièce jointe ajoutée");
    } catch {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (attId) => {
    try {
      await api.delete(`/dossiers/${dossierId}/tab-data/${tab}/attachments/${attId}`);
      onChange?.();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const token = typeof window !== "undefined" ? window.localStorage.getItem("norea_token") : "";

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {(items || []).map((a) => (
        <a
          key={a.id}
          href={`${API}/dossiers/${dossierId}/tab-data/${tab}/attachments/${a.id}`}
          onClick={(e) => {
            e.preventDefault();
            fetch(`${API}/dossiers/${dossierId}/tab-data/${tab}/attachments/${a.id}`, { headers: { Authorization: `Bearer ${token}` } })
              .then((r) => r.blob())
              .then((b) => {
                const url = URL.createObjectURL(b);
                const w = window.open(url);
                // Revoke after a safe delay so the window had time to render it
                setTimeout(() => URL.revokeObjectURL(url), 60000);
                return w;
              });
          }}
          className="group inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-norea-border bg-white hover:bg-norea-surfaceHover text-[10px] text-norea-text no-underline"
          data-testid={`pj-${fieldId}-${a.id}`}
          title={`${a.filename} (${Math.round((a.size || 0) / 1024)} Ko)`}
        >
          <FileText className="w-3 h-3 text-norea-primary flex-shrink-0" />
          <span className="truncate max-w-[80px]">{a.filename}</span>
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(a.id); }} className="opacity-50 hover:opacity-100 hover:text-red-500">
            <X className="w-3 h-3" />
          </button>
        </a>
      ))}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-dashed border-norea-border text-[10px] text-norea-textMuted hover:bg-norea-surfaceHover"
        data-testid={`pj-add-${fieldId}`}
      >
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
        Ajouter PJ
      </button>
      <input ref={inputRef} type="file" onChange={onFile} className="hidden" />
    </div>
  );
}

function FieldCell({ f, data, dossierId, tab, setValue, setStatus, reload, phones }) {
  if (f.type === "files" || f.type === "call") {
    return (
      <div className={`space-y-1.5 ${f.width === "full" ? "lg:col-span-2" : ""}`} data-testid={`field-${f.id}`}>
        <label className="text-xs font-medium text-norea-text">{f.label}</label>
        {f.type === "call"
          ? <CallClientButton dossierId={dossierId} phones={phones} />
          : <FilesField field={f} dossierId={dossierId} tab={tab} items={data?.attachments?.[f.id] || []} onChange={reload} />}
      </div>
    );
  }
  if (f.type === "checkbox") {
    return (
      <div className="lg:col-span-2" data-testid={`field-${f.id}`}>
        <FieldInput field={f} value={data?.values?.[f.id]} onChange={(v) => setValue(f.id, v)} testid={`field-input-${f.id}`} />
      </div>
    );
  }
  return (
    <div className={`space-y-1 ${f.width === "full" ? "lg:col-span-2" : ""}`} data-testid={`field-${f.id}`}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-medium text-norea-text inline-flex items-center gap-1">{f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}{f.binding && <Link2 className="w-3 h-3 text-norea-textDisabled" title="Champ relié : même donnée que la Synthèse et l'en-tête" />}</label>
        <FieldStatusInput field={f} value={data?.field_statuses?.[f.id]} onChange={(v) => setStatus(f.id, v)} testid={`field-status-${f.id}`} />
      </div>
      <FieldInput field={f} value={data?.values?.[f.id]} onChange={(v) => setValue(f.id, v)} testid={`field-input-${f.id}`} />
      {f.help && <div className="text-[10px] text-norea-textMuted" data-testid={`field-help-${f.id}`}>{f.help}</div>}
      {f.required && (data?.values?.[f.id] ?? "") === "" && <div className="text-[10px] text-red-600" data-testid={`field-required-${f.id}`}>Champ obligatoire</div>}
      <AttachmentList dossierId={dossierId} tab={tab} fieldId={f.id} items={data?.attachments?.[f.id] || []} onChange={reload} />
    </div>
  );
}

function FieldBlock({ title, icon: Icon, fields, action, testid, ...cellProps }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      {(title || action) && (
        <div className="mb-4 flex items-center justify-between gap-2">
          {title && <div className="overline flex items-center gap-1.5">{Icon && <Icon className="w-3 h-3" />} {title}</div>}
          {action}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {fields.map((f) => <FieldCell key={f.id} f={f} {...cellProps} />)}
      </div>
    </div>
  );
}

const CO_EMP_ADDR = ["co_emp_adresse", "co_emp_code_postal", "co_emp_ville"];

function CoEmprunteurBlock({ fields, ...cellProps }) {
  const v = cellProps.data?.values || {};
  if (!v.co_emp_actif) {
    return (
      <button type="button" onClick={() => cellProps.setValue("co_emp_actif", true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded border border-dashed border-norea-border text-xs font-medium text-norea-primary hover:bg-norea-surfaceHover transition-colors" data-testid="add-co-emprunteur-btn">
        <UserPlus className="w-3.5 h-3.5" /> Ajouter un co-emprunteur
      </button>
    );
  }
  const sameAddr = v.co_emp_meme_adresse !== false;
  const shown = fields.filter((f) => f.id !== "co_emp_actif" && !(sameAddr && CO_EMP_ADDR.includes(f.id)));
  const remove = (
    <button type="button" onClick={() => cellProps.setValue("co_emp_actif", false)} className="inline-flex items-center gap-1 text-[11px] text-norea-textMuted hover:text-red-600 transition-colors" data-testid="remove-co-emprunteur-btn">
      <UserMinus className="w-3 h-3" /> Retirer
    </button>
  );
  return <FieldBlock title="Co-emprunteur" icon={Users} fields={shown} action={remove} testid="block-co-emprunteur" {...cellProps} />;
}

export default function TabContentEditor({ dossierId, tab, produit, phones, onLinkedChange }) {
  const [schema, setSchema] = useState(null);
  const [data, setData] = useState(null);
  const materiel = useMateriel();
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const saveTimerRef = useRef(null);
  const savedTimerRef = useRef(null);
  const pendingSaveRef = useRef({ values: {}, field_statuses: {} });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const schemaUrl = `/configuration/tab-schema/${tab}${tab === "installation" && produit ? `?produit=${encodeURIComponent(produit)}` : ""}`;
      const [s, d] = await Promise.all([
        api.get(schemaUrl).then((r) => r.data),
        api.get(`/dossiers/${dossierId}/tab-data/${tab}`).then((r) => r.data),
      ]);
      setSchema(s);
      setData({ ...d, values: { ...(d.values || {}), ...pendingSaveRef.current.values } });
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  }, [dossierId, tab, produit]);

  useEffect(() => { load(); }, [load]);

  const flushSave = async () => {
    const payload = pendingSaveRef.current;
    pendingSaveRef.current = { values: {}, field_statuses: {} };
    if (!Object.keys(payload.values).length && !Object.keys(payload.field_statuses).length) return;
    setSaveState("saving");
    try {
      await api.patch(`/dossiers/${dossierId}/tab-data/${tab}`, payload);
      setSaveState("saved");
      const keys = Object.keys(payload.values);
      const fields = schema?.fields || [];
      const computedSources = fields.filter((f) => f.computed).flatMap((f) => f.computed.replace("sum:", "").split(","));
      if (keys.some((k) => computedSources.includes(k))) {
        const { data: d } = await api.get(`/dossiers/${dossierId}/tab-data/${tab}`);
        setData((prev) => ({ ...prev, values: { ...prev.values, ...Object.fromEntries(fields.filter((f) => f.computed).map((f) => [f.id, d.values[f.id]])) } }));
      }
      if (keys.some((k) => fields.find((f) => f.id === k)?.binding)) onLinkedChange?.();
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveState("idle"), 1500);
    } catch {
      setSaveState("idle");
      toast.error("Sauvegarde échouée");
    }
  };

  const scheduleSave = () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSave, SAVE_DEBOUNCE_MS);
  };

  const setValue = (fieldId, value) => {
    const next = { ...(data?.values || {}), [fieldId]: value };
    const extra = tab === "installation" ? pvAutofill(next, fieldId, materiel) : {};
    setData((d) => ({ ...d, values: { ...(d?.values || {}), [fieldId]: value, ...extra } }));
    Object.assign(pendingSaveRef.current.values, { [fieldId]: value, ...extra });
    scheduleSave();
  };
  const setStatus = (fieldId, status) => {
    setData((d) => ({ ...d, field_statuses: { ...(d?.field_statuses || {}), [fieldId]: status } }));
    pendingSaveRef.current.field_statuses[fieldId] = status;
    scheduleSave();
  };

  // Flush on unmount
  useEffect(() => () => { if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); flushSave(); } }, []);

  if (loading) {
    return <div className="p-8 text-center text-norea-textMuted"><Loader2 className="w-5 h-5 inline animate-spin" /></div>;
  }
  if (!schema) return null;

  const benef = schema.fields.filter((f) => f.group === "beneficiaire" && isVisible(f, data?.values, produit));
  const coEmp = schema.fields.filter((f) => f.group === "co_emprunteur" && isVisible(f, data?.values, produit));
  const visible = schema.fields.filter((f) => isVisible(f, data?.values, produit));
  const pvWarn = tab === "installation" ? pvWarning(data?.values || {}, materiel) : null;
  const rest = visible.filter((f) => !f.group && !f.section);
  const sections = [...new Set(visible.filter((f) => !f.group && f.section).map((f) => f.section))];
  const others = visible.filter((f) => f.group === "autres");
  const cellProps = { data, dossierId, tab, setValue, setStatus, reload: () => load(true), phones };

  return (
    <div data-testid={`tab-content-${tab}`}>
      <div className="flex items-center justify-between">
        <TabWorkflowBar
          steps={schema.workflow || []}
          statuses={data?.workflow_statuses || {}}
          dossierId={dossierId}
          tab={tab}
          onChange={load}
        />
        {saveState !== "idle" && (
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border inline-flex items-center gap-1 ${
              saveState === "saving"
                ? "bg-amber-50 text-amber-800 border-amber-300"
                : "bg-emerald-50 text-emerald-800 border-emerald-400"
            }`}
            data-testid={`save-indicator-${tab}`}
          >
            {saveState === "saving" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : "✓"}
            {saveState === "saving" ? "Enregistrement…" : "Enregistré"}
          </span>
        )}
      </div>

      {schema.fields.length === 0 ? (
        <div className="card-flat p-6 text-center text-xs text-norea-textMuted italic">
          Aucun champ défini pour cet onglet. Édite le schéma depuis <strong>Paramètres</strong> pour en ajouter.
        </div>
      ) : (
        <div className="space-y-4">
          {benef.length > 0 && <FieldBlock title="Bénéficiaire" icon={User} fields={benef} testid="block-beneficiaire" {...cellProps} />}
          {coEmp.length > 0 && <CoEmprunteurBlock fields={coEmp} {...cellProps} />}
          {pvWarn && <div className="lg:col-span-2 px-3 py-2 rounded border border-amber-300 bg-amber-50 text-xs text-amber-800" data-testid="pv-compat-warning">{pvWarn}</div>}
          {rest.length > 0 && <FieldBlock title={benef.length ? "Finance" : null} icon={Banknote} fields={rest} testid={`block-${tab}-main`} {...cellProps} />}
          {sections.map((sec) => <FieldBlock key={sec} title={sec} fields={visible.filter((f) => !f.group && f.section === sec)} testid={`block-${tab}-section-${sec}`} {...cellProps} />)}
          {others.length > 0 && <FieldBlock title="Autres informations" fields={others} testid={`block-${tab}-autres`} {...cellProps} />}
        </div>
      )}
    </div>
  );
}
